import { Observable } from "@babylonjs/core/Misc/observable";

import type { GameAction } from "./actions";
import { DEFAULT_KEY_BINDINGS, PREVENT_DEFAULT_CODES } from "./actions";

/**
 * Traduit les évènements clavier — et les sources analogiques (joystick
 * virtuel, manette) — en actions de jeu.
 *
 * Les scènes interrogent un état (`isActive`, `strength`, `axis`) plutôt que
 * d'écouter le DOM : le rendu est ainsi découplé de la source d'entrée.
 *
 * Une touche vaut « intensité 1 » ; une source analogique publie une intensité
 * continue dans [0, 1]. Les deux se combinent par le maximum, de sorte qu'une
 * scène n'a jamais à savoir d'où vient le déplacement.
 */
export class InputManager {
  /** Émis quand l'action passe de relâchée à engagée (toutes sources confondues). */
  readonly onActionPressed = new Observable<GameAction>();
  readonly onActionReleased = new Observable<GameAction>();

  private readonly bindings: Readonly<Record<string, GameAction>>;
  /** Actions maintenues au clavier — intensité implicite de 1. */
  private readonly held = new Set<GameAction>();
  /** Intensités publiées par les sources analogiques, dans ]0, 1]. */
  private readonly analog = new Map<GameAction, number>();
  /** État combiné : ce que voient `isActive` et les observateurs. */
  private readonly engaged = new Set<GameAction>();
  private target: (Window & typeof globalThis) | HTMLElement | null = null;

  constructor(bindings: Readonly<Record<string, GameAction>> = DEFAULT_KEY_BINDINGS) {
    this.bindings = bindings;
  }

  /** L'action est-elle engagée à cet instant, quelle qu'en soit la source ? */
  isActive(action: GameAction): boolean {
    return this.engaged.has(action);
  }

  /**
   * Intensité de l'action dans [0, 1] : 1 pour une touche, valeur continue pour
   * une source analogique. Permet un déplacement proportionnel à l'inclinaison
   * du manche.
   */
  strength(action: GameAction): number {
    if (this.held.has(action)) return 1;
    return this.analog.get(action) ?? 0;
  }

  /**
   * Axe normalisé dans [-1, 1] à partir de deux actions opposées.
   * Les deux engagées simultanément se compensent.
   */
  axis(negative: GameAction, positive: GameAction): number {
    return this.strength(positive) - this.strength(negative);
  }

  /**
   * Publie l'intensité d'une action depuis une source analogique.
   *
   * L'appelant réapplique la valeur à chaque changement ; 0 relâche sa
   * contribution sans toucher au clavier, qui reste prioritaire tant qu'une
   * touche est maintenue.
   */
  setAnalogStrength(action: GameAction, strength: number): void {
    const clamped = Math.min(Math.max(strength, 0), 1);

    if (clamped === 0) {
      if (!this.analog.delete(action)) return;
    } else {
      if (this.analog.get(action) === clamped) return;
      this.analog.set(action, clamped);
    }

    this.refresh(action);
  }

  attach(target: (Window & typeof globalThis) | HTMLElement): void {
    if (this.target) this.detach();
    this.target = target;

    target.addEventListener("keydown", this.handleKeyDown);
    target.addEventListener("keyup", this.handleKeyUp);
    // Sans cela, une touche maintenue au moment d'un Alt+Tab reste "enfoncée"
    // à vie : le keyup part vers une autre fenêtre et ne nous parvient jamais.
    window.addEventListener("blur", this.handleBlur);
  }

  detach(): void {
    if (!this.target) return;

    this.target.removeEventListener("keydown", this.handleKeyDown);
    this.target.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.handleBlur);
    this.target = null;
    this.reset();
  }

  /** Relâche toutes les actions en cours, clavier comme sources analogiques. */
  reset(): void {
    this.held.clear();
    this.analog.clear();

    for (const action of this.engaged) {
      this.onActionReleased.notifyObservers(action);
    }
    this.engaged.clear();
  }

  dispose(): void {
    this.detach();
    this.onActionPressed.clear();
    this.onActionReleased.clear();
  }

  /**
   * Recalcule l'état combiné d'une action et notifie les seules transitions.
   * Point de passage unique : sans lui, relâcher une touche pendant que le
   * joystick pousse dans la même direction émettrait un faux `released`.
   */
  private refresh(action: GameAction): void {
    const active = this.strength(action) > 0;

    if (active === this.engaged.has(action)) return;

    if (active) {
      this.engaged.add(action);
      this.onActionPressed.notifyObservers(action);
    } else {
      this.engaged.delete(action);
      this.onActionReleased.notifyObservers(action);
    }
  }

  // Champs fléchés : `this` reste lié, et la même référence sert à
  // add/removeEventListener.
  private readonly handleKeyDown = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) return;

    if (PREVENT_DEFAULT_CODES.has(event.code)) {
      event.preventDefault();
    }

    const action = this.bindings[event.code];
    if (!action) return;

    // `repeat` filtre l'auto-répétition du système : on ne veut qu'un front
    // montant par appui réel.
    if (event.repeat || this.held.has(action)) return;

    this.held.add(action);
    this.refresh(action);
  };

  private readonly handleKeyUp = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) return;

    const action = this.bindings[event.code];
    if (!action) return;

    // Une action liée à plusieurs touches ne se relâche que si aucune autre
    // touche de cette action n'est encore maintenue — non suivi ici, on relâche
    // au premier keyup, ce qui est le comportement attendu dans la majorité des
    // cas et évite de conserver un état par touche.
    if (!this.held.delete(action)) return;
    this.refresh(action);
  };

  private readonly handleBlur = (): void => {
    this.reset();
  };
}
